import type { CaseEvent } from "../../api/types.ts"
import { useT } from "../../i18n/index.tsx"
import { OWNER_ICON, ownerOfEvent } from "../../lib/case-owner.ts"
import { EmptyState, Icon } from "../../ui/index.ts"
import { OwnerTag } from "../badges.tsx"
import { formatDateTime } from "../format.ts"

/**
 * 무엇이 언제 누구에 의해 일어났는지. 서버가 남긴 감사 로그를 그대로 보여 준다.
 *
 * 항목마다 **어느 쪽 일이었는지**를 점 색과 이름표로 표시한다(`lib/case-owner.ts`) —
 * 두 회사가 번갈아 손대는 기록이라, 색이 바뀌는 자리가 곧 일이 넘어간 자리다.
 * 모르는 이벤트(나중에 생긴 것)는 색 없이 예전 모양 그대로 그린다.
 */
export function HistoryTab({ events }: { events: CaseEvent[] }) {
  const t = useT()
  if (events.length === 0) {
    return <EmptyState icon="clock" title={t("caseDetail.historyEmpty")} />
  }

  return (
    <ol className="timeline">
      {events.map((event) => {
        const owner = ownerOfEvent(event.type)
        return (
          <li className="timeline__item" key={event.id}>
            <span className={owner ? `timeline__dot timeline__dot--${owner}` : "timeline__dot"}>
              <Icon name={owner ? OWNER_ICON[owner] : "check"} size={11} />
            </span>
            <div className="timeline__body">
              <span className="timeline__what">
                {t(`event.${event.type}`)}
                {owner ? <OwnerTag owner={owner} /> : null}
              </span>
              <span className="timeline__meta">
                <time dateTime={event.createdAt}>{formatDateTime(event.createdAt)}</time>
                {event.actorName ? <> · {event.actorName}</> : null}
              </span>
            </div>
          </li>
        )
      })}
    </ol>
  )
}
